// src/components/PortalConfig.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'react-toastify';
import './PortalConfig.css';
import {
  FaArrowLeft, FaPlus, FaTrash, FaPen, FaSave, FaTimes, FaSpinner,
  FaGripVertical, FaEye, FaEyeSlash, FaImage, FaSignature, FaPoll,
  FaThLarge, FaNewspaper, FaBullhorn, FaAddressBook, FaFileAlt, FaLink,
  FaCalendarAlt, FaQuestionCircle, FaDoorOpen, FaCheck, FaExclamationTriangle,
} from 'react-icons/fa';

/* ============================================================================
 * Helpers de data para <input type="datetime-local" | "date">
 * ========================================================================== */
const toInputDateTime = (v) => {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
const toInputDate = (v) => {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
// datetime-local → 'YYYY-MM-DD HH:MM:SS' que o MySQL aceita
const fromInputDateTime = (v) => (v ? String(v).replace('T', ' ') + ':00' : null);

/* ============================================================================
 * Specs dos recursos "simples" (espelham a whitelist do backend)
 * type: text | textarea | select | bool | date | datetime | color | number
 * ========================================================================== */
const RESOURCES = {
  noticias: {
    label: 'Notícias', icon: <FaNewspaper />,
    colunas: ['titulo', 'categoria', 'publicado'],
    campos: [
      { key: 'titulo', label: 'Título', type: 'text', req: true },
      { key: 'subtitulo', label: 'Subtítulo', type: 'text' },
      { key: 'corpo', label: 'Texto', type: 'textarea' },
      { key: 'imagem_url', label: 'URL da imagem', type: 'text' },
      { key: 'categoria', label: 'Categoria', type: 'text' },
      { key: 'publicado_em', label: 'Publicar em', type: 'datetime' },
      { key: 'fixado', label: 'Fixar no topo', type: 'bool' },
      { key: 'publicado', label: 'Publicado', type: 'bool' },
    ],
  },
  comunicados: {
    label: 'Comunicados', icon: <FaBullhorn />,
    colunas: ['titulo', 'prioridade', 'publicado'],
    campos: [
      { key: 'titulo', label: 'Título', type: 'text', req: true },
      { key: 'corpo', label: 'Texto', type: 'textarea' },
      { key: 'prioridade', label: 'Prioridade', type: 'select', options: ['baixa', 'media', 'alta', 'urgente'] },
      { key: 'vigente_ate', label: 'Vigente até', type: 'date' },
      { key: 'fixado', label: 'Fixar', type: 'bool' },
      { key: 'publicado', label: 'Publicado', type: 'bool' },
    ],
  },
  contatos: {
    label: 'Ramais', icon: <FaAddressBook />,
    colunas: ['nome', 'setor', 'ramal'],
    campos: [
      { key: 'nome', label: 'Nome', type: 'text', req: true },
      { key: 'setor', label: 'Setor', type: 'text' },
      { key: 'cargo', label: 'Cargo', type: 'text' },
      { key: 'ramal', label: 'Ramal', type: 'text' },
      { key: 'telefone', label: 'Telefone', type: 'text' },
      { key: 'whatsapp', label: 'WhatsApp', type: 'text' },
      { key: 'email', label: 'E-mail', type: 'text' },
      { key: 'foto_url', label: 'URL da foto', type: 'text' },
      { key: 'ordem', label: 'Ordem', type: 'number' },
    ],
  },
  documentos: {
    label: 'Documentos', icon: <FaFileAlt />,
    colunas: ['titulo', 'categoria', 'tipo'],
    campos: [
      { key: 'titulo', label: 'Título', type: 'text', req: true },
      { key: 'descricao', label: 'Descrição', type: 'textarea' },
      { key: 'categoria', label: 'Categoria', type: 'text' },
      { key: 'drive_url', label: 'Link do Drive', type: 'text', req: true },
      { key: 'tipo', label: 'Tipo (pdf, doc…)', type: 'text' },
      { key: 'obrigatorio_leitura', label: 'Leitura obrigatória', type: 'bool' },
      { key: 'ordem', label: 'Ordem', type: 'number' },
    ],
  },
  links: {
    label: 'Links Rápidos', icon: <FaLink />,
    colunas: ['label', 'categoria', 'url'],
    campos: [
      { key: 'label', label: 'Rótulo', type: 'text', req: true },
      { key: 'url', label: 'URL', type: 'text', req: true },
      { key: 'descricao', label: 'Descrição', type: 'text' },
      { key: 'categoria', label: 'Categoria', type: 'text' },
      { key: 'cor', label: 'Cor', type: 'color' },
      { key: 'abre_nova_aba', label: 'Abrir em nova aba', type: 'bool' },
      { key: 'ordem', label: 'Ordem', type: 'number' },
    ],
  },
  eventos: {
    label: 'Eventos', icon: <FaCalendarAlt />,
    colunas: ['titulo', 'tipo', 'data_inicio'],
    campos: [
      { key: 'titulo', label: 'Título', type: 'text', req: true },
      { key: 'descricao', label: 'Descrição', type: 'textarea' },
      { key: 'tipo', label: 'Tipo', type: 'select', options: ['feriado', 'evento', 'reuniao', 'treinamento', 'aniversario_empresa', 'outro'] },
      { key: 'data_inicio', label: 'Início', type: 'datetime', req: true },
      { key: 'data_fim', label: 'Fim', type: 'datetime' },
      { key: 'dia_inteiro', label: 'Dia inteiro', type: 'bool' },
      { key: 'local', label: 'Local', type: 'text' },
      { key: 'cor', label: 'Cor', type: 'color' },
    ],
  },
  faq: {
    label: 'FAQ', icon: <FaQuestionCircle />,
    colunas: ['pergunta', 'categoria', 'publicado'],
    campos: [
      { key: 'pergunta', label: 'Pergunta', type: 'text', req: true },
      { key: 'resposta', label: 'Resposta', type: 'textarea' },
      { key: 'categoria', label: 'Categoria', type: 'text' },
      { key: 'ordem', label: 'Ordem', type: 'number' },
      { key: 'publicado', label: 'Publicado', type: 'bool' },
    ],
  },
  recursos: {
    label: 'Recursos (Reservas)', icon: <FaDoorOpen />,
    colunas: ['nome', 'tipo', 'localizacao'],
    campos: [
      { key: 'nome', label: 'Nome', type: 'text', req: true },
      { key: 'tipo', label: 'Tipo', type: 'select', options: ['sala', 'veiculo', 'equipamento', 'outro'] },
      { key: 'descricao', label: 'Descrição', type: 'textarea' },
      { key: 'localizacao', label: 'Localização', type: 'text' },
      { key: 'capacidade', label: 'Capacidade', type: 'number' },
      { key: 'cor', label: 'Cor', type: 'color' },
      { key: 'requer_aprovacao', label: 'Requer aprovação', type: 'bool' },
    ],
  },
};

// Todos os widgets (para o painel de Layout), incluindo os sem tabela
const WIDGETS_LAYOUT = [
  { key: 'comunicados', label: 'Comunicados' },
  { key: 'noticias', label: 'Mural de Notícias' },
  { key: 'cotacao', label: 'Câmbio (dólar)' },
  { key: 'aniversariantes', label: 'Aniversariantes' },
  { key: 'assinatura', label: 'Assinatura de e-mail' },
  { key: 'contatos', label: 'Ramais & Contatos' },
  { key: 'documentos', label: 'Documentos' },
  { key: 'links', label: 'Links Rápidos' },
  { key: 'eventos', label: 'Agenda & Eventos' },
  { key: 'faq', label: 'Central de Ajuda' },
  { key: 'enquetes', label: 'Enquetes' },
  { key: 'reservas', label: 'Reserva de Recursos' },
  { key: 'indicadores', label: 'Indicadores de Frota' },
];

/* ============================================================================
 * Campo de formulário genérico
 * ========================================================================== */
function Campo({ campo, valor, onChange }) {
  const comum = { value: valor ?? '', onChange: (e) => onChange(campo.key, e.target.value) };
  if (campo.type === 'textarea') return <textarea rows={4} {...comum} />;
  if (campo.type === 'select') {
    return (
      <select {...comum}>
        {campo.options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (campo.type === 'bool') {
    return (
      <label className="pcfg-switch">
        <input type="checkbox" checked={!!valor} onChange={(e) => onChange(campo.key, e.target.checked)} />
        <span className="pcfg-switch__slider" />
      </label>
    );
  }
  if (campo.type === 'color') {
    return (
      <div className="pcfg-color">
        <input type="color" value={valor || '#0d7377'} onChange={(e) => onChange(campo.key, e.target.value)} />
        <input type="text" value={valor || ''} placeholder="#0d7377" onChange={(e) => onChange(campo.key, e.target.value)} />
      </div>
    );
  }
  if (campo.type === 'number') return <input type="number" {...comum} />;
  if (campo.type === 'date') return <input type="date" value={toInputDate(valor)} onChange={(e) => onChange(campo.key, e.target.value)} />;
  if (campo.type === 'datetime') return <input type="datetime-local" value={toInputDateTime(valor)} onChange={(e) => onChange(campo.key, e.target.value)} />;
  return <input type="text" {...comum} />;
}

/* ============================================================================
 * Gerenciador genérico de recurso (lista + formulário)
 * ========================================================================== */
function GerenciadorRecurso({ recurso }) {
  const spec = RESOURCES[recurso];
  const [itens, setItens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editando, setEditando] = useState(null); // objeto em edição/criação
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(() => {
    setLoading(true);
    api.get(`/api/portal/${recurso}?manage=1`)
      .then(({ data }) => setItens(Array.isArray(data) ? data : []))
      .catch(() => setItens([]))
      .finally(() => setLoading(false));
  }, [recurso]);
  useEffect(() => { carregar(); }, [carregar]);

  const novo = () => {
    const base = {};
    spec.campos.forEach((c) => { base[c.key] = c.type === 'bool' ? false : ''; });
    setEditando(base);
  };
  const editar = (item) => setEditando({ ...item });
  const setCampo = (key, val) => setEditando((p) => ({ ...p, [key]: val }));

  const preparaPayload = (obj) => {
    const out = {};
    spec.campos.forEach((c) => {
      let v = obj[c.key];
      if (c.type === 'datetime') v = fromInputDateTime(v);
      if (c.type === 'bool') v = !!v;
      out[c.key] = v === '' ? null : v;
    });
    return out;
  };

  const salvar = async () => {
    for (const c of spec.campos) {
      if (c.req && !String(editando[c.key] ?? '').trim()) {
        return toast.warn(`Preencha o campo "${c.label}".`);
      }
    }
    setSalvando(true);
    try {
      const payload = preparaPayload(editando);
      if (editando.id) await api.put(`/api/portal/${recurso}/${editando.id}`, payload);
      else await api.post(`/api/portal/${recurso}`, payload);
      toast.success('Salvo!');
      setEditando(null);
      carregar();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Falha ao salvar.');
    } finally {
      setSalvando(false);
    }
  };

  const excluir = async (item) => {
    if (!window.confirm(`Remover "${item[spec.campos[0].key]}"?`)) return;
    try {
      await api.delete(`/api/portal/${recurso}/${item.id}`);
      toast.info('Item removido.');
      carregar();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Falha ao remover.');
    }
  };

  return (
    <div className="pcfg-panel">
      <div className="pcfg-panel__bar">
        <h2>{spec.icon} {spec.label}</h2>
        <button className="pcfg-btn-primary" onClick={novo}><FaPlus /> Novo</button>
      </div>

      {loading ? (
        <div className="pcfg-loading"><FaSpinner className="pcfg-spin" /> Carregando…</div>
      ) : itens.length === 0 ? (
        <div className="pcfg-empty">Nenhum item cadastrado ainda.</div>
      ) : (
        <table className="pcfg-table">
          <thead>
            <tr>
              {spec.colunas.map((c) => <th key={c}>{spec.campos.find((f) => f.key === c)?.label || c}</th>)}
              <th className="pcfg-table__acoes">Ações</th>
            </tr>
          </thead>
          <tbody>
            {itens.map((item) => (
              <tr key={item.id} className={item.ativo === 0 ? 'is-inativo' : ''}>
                {spec.colunas.map((c) => {
                  const campo = spec.campos.find((f) => f.key === c);
                  let val = item[c];
                  if (campo?.type === 'bool') val = val ? <FaCheck className="pcfg-yes" /> : <FaTimes className="pcfg-no" />;
                  return <td key={c}>{val}</td>;
                })}
                <td className="pcfg-table__acoes">
                  <button className="pcfg-icon-btn" onClick={() => editar(item)} title="Editar"><FaPen /></button>
                  <button className="pcfg-icon-btn is-danger" onClick={() => excluir(item)} title="Remover"><FaTrash /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editando && (
        <div className="pcfg-modal-backdrop" onClick={() => setEditando(null)}>
          <div className="pcfg-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pcfg-modal__head">
              <h3>{editando.id ? 'Editar' : 'Novo'} — {spec.label}</h3>
              <button className="pcfg-icon-btn" onClick={() => setEditando(null)}><FaTimes /></button>
            </div>
            <div className="pcfg-modal__body">
              {spec.campos.map((c) => (
                <div key={c.key} className={`pcfg-field ${c.type === 'textarea' ? 'is-full' : ''} ${c.type === 'bool' ? 'is-bool' : ''}`}>
                  <label>{c.label}{c.req && <span className="pcfg-req">*</span>}</label>
                  <Campo campo={c} valor={editando[c.key]} onChange={setCampo} />
                </div>
              ))}
            </div>
            <div className="pcfg-modal__foot">
              <button className="pcfg-btn-ghost" onClick={() => setEditando(null)}>Cancelar</button>
              <button className="pcfg-btn-primary" disabled={salvando} onClick={salvar}>
                {salvando ? <><FaSpinner className="pcfg-spin" /> Salvando…</> : <><FaSave /> Salvar</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================================================================
 * Painel de LAYOUT + BANNER
 * ========================================================================== */
function PainelLayout() {
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [banner, setBanner] = useState({ banner_titulo: '', banner_subtitulo: '', banner_imagem_url: '' });
  const [widgets, setWidgets] = useState(() => WIDGETS_LAYOUT.map((w, i) => ({ ...w, enabled: true, ordem: i })));

  useEffect(() => {
    let vivo = true;
    api.get('/api/portal/config')
      .then(({ data }) => {
        if (!vivo || !data) return;
        setBanner({
          banner_titulo: data.banner_titulo || '',
          banner_subtitulo: data.banner_subtitulo || '',
          banner_imagem_url: data.banner_imagem_url || '',
        });
        let layout = data.layout_json;
        if (typeof layout === 'string') { try { layout = JSON.parse(layout); } catch { layout = null; } }
        if (Array.isArray(layout) && layout.length) {
          const mapa = new Map(layout.map((l) => [l.key, l]));
          const merged = WIDGETS_LAYOUT.map((w, i) => {
            const cfg = mapa.get(w.key);
            return { ...w, enabled: cfg ? cfg.enabled !== false : true, ordem: cfg?.ordem ?? i };
          }).sort((a, b) => a.ordem - b.ordem);
          setWidgets(merged);
        }
      })
      .finally(() => { if (vivo) setLoading(false); });
    return () => { vivo = false; };
  }, []);

  const toggle = (key) => setWidgets((ws) => ws.map((w) => w.key === key ? { ...w, enabled: !w.enabled } : w));
  const mover = (idx, dir) => setWidgets((ws) => {
    const novo = [...ws];
    const alvo = idx + dir;
    if (alvo < 0 || alvo >= novo.length) return ws;
    [novo[idx], novo[alvo]] = [novo[alvo], novo[idx]];
    return novo.map((w, i) => ({ ...w, ordem: i }));
  });

  const salvar = async () => {
    setSalvando(true);
    try {
      const layout_json = widgets.map((w, i) => ({ key: w.key, enabled: w.enabled, ordem: i }));
      await api.put('/api/portal/config', { ...banner, layout_json });
      toast.success('Layout do portal salvo!');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Falha ao salvar layout.');
    } finally {
      setSalvando(false);
    }
  };

  if (loading) return <div className="pcfg-loading"><FaSpinner className="pcfg-spin" /> Carregando…</div>;

  return (
    <div className="pcfg-panel">
      <div className="pcfg-panel__bar">
        <h2><FaThLarge /> Layout & Banner</h2>
        <button className="pcfg-btn-primary" disabled={salvando} onClick={salvar}>
          {salvando ? <><FaSpinner className="pcfg-spin" /> …</> : <><FaSave /> Salvar layout</>}
        </button>
      </div>

      <div className="pcfg-layout-cols">
        <div className="pcfg-card">
          <h4><FaImage /> Banner do topo</h4>
          <div className="pcfg-field">
            <label>Título (deixe vazio p/ saudação automática)</label>
            <input type="text" value={banner.banner_titulo} onChange={(e) => setBanner((b) => ({ ...b, banner_titulo: e.target.value }))} />
          </div>
          <div className="pcfg-field">
            <label>Subtítulo</label>
            <input type="text" value={banner.banner_subtitulo} onChange={(e) => setBanner((b) => ({ ...b, banner_subtitulo: e.target.value }))} />
          </div>
          <div className="pcfg-field">
            <label>URL da imagem de fundo</label>
            <input type="text" value={banner.banner_imagem_url} onChange={(e) => setBanner((b) => ({ ...b, banner_imagem_url: e.target.value }))} />
          </div>
        </div>

        <div className="pcfg-card">
          <h4><FaGripVertical /> Widgets (ordem e visibilidade)</h4>
          <ul className="pcfg-widget-list">
            {widgets.map((w, i) => (
              <li key={w.key} className={w.enabled ? '' : 'is-off'}>
                <div className="pcfg-widget-move">
                  <button className="pcfg-icon-btn" disabled={i === 0} onClick={() => mover(i, -1)} title="Subir">▲</button>
                  <button className="pcfg-icon-btn" disabled={i === widgets.length - 1} onClick={() => mover(i, 1)} title="Descer">▼</button>
                </div>
                <span className="pcfg-widget-name">{w.label}</span>
                <button className={`pcfg-vis-btn ${w.enabled ? 'is-on' : ''}`} onClick={() => toggle(w.key)}>
                  {w.enabled ? <><FaEye /> Visível</> : <><FaEyeSlash /> Oculto</>}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

/* ============================================================================
 * Painel de ASSINATURA
 * ========================================================================== */
function PainelAssinatura() {
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [cfg, setCfg] = useState({
    template_html: '', logo_url: '', cor_primaria: '#0d7377', cor_secundaria: '#14213d',
    empresa_nome: '', empresa_site: '', empresa_endereco: '', disclaimer: '',
  });

  useEffect(() => {
    let vivo = true;
    api.get('/api/portal/assinatura/config')
      .then(({ data }) => { if (vivo && data) setCfg((c) => ({ ...c, ...data })); })
      .finally(() => { if (vivo) setLoading(false); });
    return () => { vivo = false; };
  }, []);

  const set = (k) => (e) => setCfg((c) => ({ ...c, [k]: e.target.value }));

  const salvar = async () => {
    setSalvando(true);
    try {
      await api.put('/api/portal/assinatura/config', cfg);
      toast.success('Configuração da assinatura salva!');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Falha ao salvar.');
    } finally {
      setSalvando(false);
    }
  };

  if (loading) return <div className="pcfg-loading"><FaSpinner className="pcfg-spin" /> Carregando…</div>;

  return (
    <div className="pcfg-panel">
      <div className="pcfg-panel__bar">
        <h2><FaSignature /> Assinatura de E-mail</h2>
        <button className="pcfg-btn-primary" disabled={salvando} onClick={salvar}>
          {salvando ? <><FaSpinner className="pcfg-spin" /> …</> : <><FaSave /> Salvar</>}
        </button>
      </div>
      <div className="pcfg-card">
        <div className="pcfg-grid-2">
          <div className="pcfg-field"><label>Nome da empresa</label><input type="text" value={cfg.empresa_nome || ''} onChange={set('empresa_nome')} /></div>
          <div className="pcfg-field"><label>Site</label><input type="text" value={cfg.empresa_site || ''} onChange={set('empresa_site')} /></div>
          <div className="pcfg-field"><label>URL do logo</label><input type="text" value={cfg.logo_url || ''} onChange={set('logo_url')} /></div>
          <div className="pcfg-field"><label>Endereço</label><input type="text" value={cfg.empresa_endereco || ''} onChange={set('empresa_endereco')} /></div>
          <div className="pcfg-field">
            <label>Cor primária</label>
            <div className="pcfg-color">
              <input type="color" value={cfg.cor_primaria || '#0d7377'} onChange={set('cor_primaria')} />
              <input type="text" value={cfg.cor_primaria || ''} onChange={set('cor_primaria')} />
            </div>
          </div>
          <div className="pcfg-field">
            <label>Cor secundária</label>
            <div className="pcfg-color">
              <input type="color" value={cfg.cor_secundaria || '#14213d'} onChange={set('cor_secundaria')} />
              <input type="text" value={cfg.cor_secundaria || ''} onChange={set('cor_secundaria')} />
            </div>
          </div>
        </div>
        <div className="pcfg-field is-full"><label>Disclaimer (rodapé legal)</label><textarea rows={3} value={cfg.disclaimer || ''} onChange={set('disclaimer')} /></div>
        <div className="pcfg-field is-full">
          <label>Template HTML (opcional — sobrescreve o modelo padrão)</label>
          <textarea rows={5} value={cfg.template_html || ''} onChange={set('template_html')} placeholder="Deixe vazio para usar o modelo padrão do portal." />
        </div>
      </div>
    </div>
  );
}

/* ============================================================================
 * Painel de ENQUETES (com opções)
 * ========================================================================== */
function PainelEnquetes() {
  const [itens, setItens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editando, setEditando] = useState(null);
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(() => {
    setLoading(true);
    api.get('/api/portal/enquetes?manage=1')
      .then(({ data }) => setItens(Array.isArray(data) ? data : []))
      .catch(() => setItens([]))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  const nova = () => setEditando({ titulo: '', descricao: '', tipo: 'unica', status: 'rascunho', anonima: true, opcoes: [{ texto: '' }, { texto: '' }] });

  const setCampo = (k, v) => setEditando((p) => ({ ...p, [k]: v }));
  const setOpcao = (i, v) => setEditando((p) => ({ ...p, opcoes: p.opcoes.map((o, idx) => idx === i ? { ...o, texto: v } : o) }));
  const addOpcao = () => setEditando((p) => ({ ...p, opcoes: [...p.opcoes, { texto: '' }] }));
  const rmOpcao = (i) => setEditando((p) => ({ ...p, opcoes: p.opcoes.filter((_, idx) => idx !== i) }));

  const salvar = async () => {
    if (!editando.titulo.trim()) return toast.warn('Informe o título.');
    if (!editando.id) {
      const validas = editando.opcoes.filter((o) => o.texto.trim());
      if (validas.length < 2) return toast.warn('Informe ao menos 2 opções.');
    }
    setSalvando(true);
    try {
      if (editando.id) {
        // edição: só metadados/status (opções não são alteradas aqui)
        await api.put(`/api/portal/enquetes/${editando.id}`, {
          titulo: editando.titulo, descricao: editando.descricao, status: editando.status, encerra_em: editando.encerra_em || null,
        });
      } else {
        await api.post('/api/portal/enquetes', {
          titulo: editando.titulo, descricao: editando.descricao, tipo: editando.tipo,
          anonima: editando.anonima, status: editando.status,
          opcoes: editando.opcoes.filter((o) => o.texto.trim()),
        });
      }
      toast.success('Enquete salva!');
      setEditando(null);
      carregar();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Falha ao salvar enquete.');
    } finally {
      setSalvando(false);
    }
  };

  const excluir = async (enq) => {
    if (!window.confirm(`Remover a enquete "${enq.titulo}"?`)) return;
    try {
      await api.delete(`/api/portal/enquetes/${enq.id}`);
      toast.info('Enquete removida.');
      carregar();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Falha ao remover.');
    }
  };

  return (
    <div className="pcfg-panel">
      <div className="pcfg-panel__bar">
        <h2><FaPoll /> Enquetes</h2>
        <button className="pcfg-btn-primary" onClick={nova}><FaPlus /> Nova enquete</button>
      </div>

      {loading ? (
        <div className="pcfg-loading"><FaSpinner className="pcfg-spin" /> Carregando…</div>
      ) : itens.length === 0 ? (
        <div className="pcfg-empty">Nenhuma enquete cadastrada.</div>
      ) : (
        <table className="pcfg-table">
          <thead><tr><th>Título</th><th>Tipo</th><th>Status</th><th className="pcfg-table__acoes">Ações</th></tr></thead>
          <tbody>
            {itens.map((enq) => (
              <tr key={enq.id}>
                <td>{enq.titulo}</td>
                <td>{enq.tipo}</td>
                <td><span className={`pcfg-status pcfg-status--${enq.status}`}>{enq.status}</span></td>
                <td className="pcfg-table__acoes">
                  <button className="pcfg-icon-btn" onClick={() => setEditando({ ...enq, opcoes: enq.opcoes || [] })} title="Editar"><FaPen /></button>
                  <button className="pcfg-icon-btn is-danger" onClick={() => excluir(enq)} title="Remover"><FaTrash /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editando && (
        <div className="pcfg-modal-backdrop" onClick={() => setEditando(null)}>
          <div className="pcfg-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pcfg-modal__head">
              <h3>{editando.id ? 'Editar enquete' : 'Nova enquete'}</h3>
              <button className="pcfg-icon-btn" onClick={() => setEditando(null)}><FaTimes /></button>
            </div>
            <div className="pcfg-modal__body">
              <div className="pcfg-field is-full"><label>Título<span className="pcfg-req">*</span></label>
                <input type="text" value={editando.titulo} onChange={(e) => setCampo('titulo', e.target.value)} /></div>
              <div className="pcfg-field is-full"><label>Descrição</label>
                <textarea rows={2} value={editando.descricao || ''} onChange={(e) => setCampo('descricao', e.target.value)} /></div>
              <div className="pcfg-field"><label>Status</label>
                <select value={editando.status} onChange={(e) => setCampo('status', e.target.value)}>
                  <option value="rascunho">Rascunho</option><option value="aberta">Aberta</option><option value="encerrada">Encerrada</option>
                </select>
              </div>
              {!editando.id && (
                <div className="pcfg-field"><label>Tipo de voto</label>
                  <select value={editando.tipo} onChange={(e) => setCampo('tipo', e.target.value)}>
                    <option value="unica">Escolha única</option><option value="multipla">Múltipla escolha</option>
                  </select>
                </div>
              )}

              {!editando.id && (
                <div className="pcfg-field is-full">
                  <label>Opções<span className="pcfg-req">*</span></label>
                  {editando.opcoes.map((o, i) => (
                    <div key={i} className="pcfg-opcao-row">
                      <input type="text" value={o.texto} placeholder={`Opção ${i + 1}`} onChange={(e) => setOpcao(i, e.target.value)} />
                      {editando.opcoes.length > 2 && (
                        <button className="pcfg-icon-btn is-danger" onClick={() => rmOpcao(i)}><FaTimes /></button>
                      )}
                    </div>
                  ))}
                  <button className="pcfg-btn-ghost pcfg-btn-sm" onClick={addOpcao}><FaPlus /> Adicionar opção</button>
                </div>
              )}
              {editando.id && (
                <p className="pcfg-hint"><FaExclamationTriangle /> As opções não podem ser editadas após a criação (preservam os votos). Para mudar opções, crie uma nova enquete.</p>
              )}
            </div>
            <div className="pcfg-modal__foot">
              <button className="pcfg-btn-ghost" onClick={() => setEditando(null)}>Cancelar</button>
              <button className="pcfg-btn-primary" disabled={salvando} onClick={salvar}>
                {salvando ? <><FaSpinner className="pcfg-spin" /> Salvando…</> : <><FaSave /> Salvar</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================================================================
 * COMPONENTE PRINCIPAL — navegação por abas
 * ========================================================================== */
const ABAS = [
  { key: 'layout', label: 'Layout & Banner', icon: <FaThLarge /> },
  { key: 'noticias', label: 'Notícias', icon: <FaNewspaper /> },
  { key: 'comunicados', label: 'Comunicados', icon: <FaBullhorn /> },
  { key: 'enquetes', label: 'Enquetes', icon: <FaPoll /> },
  { key: 'contatos', label: 'Ramais', icon: <FaAddressBook /> },
  { key: 'links', label: 'Links', icon: <FaLink /> },
  { key: 'documentos', label: 'Documentos', icon: <FaFileAlt /> },
  { key: 'eventos', label: 'Eventos', icon: <FaCalendarAlt /> },
  { key: 'faq', label: 'FAQ', icon: <FaQuestionCircle /> },
  { key: 'recursos', label: 'Recursos', icon: <FaDoorOpen /> },
  { key: 'assinatura', label: 'Assinatura', icon: <FaSignature /> },
];

export default function PortalConfig() {
  const navigate = useNavigate();
  const [aba, setAba] = useState('layout');
  const [checando, setChecando] = useState(true);
  const [autorizado, setAutorizado] = useState(false);

  useEffect(() => {
    let vivo = true;
    api.get('/api/portal/contexto')
      .then(({ data }) => { if (vivo) setAutorizado(!!data?.podeConfigurar); })
      .catch(() => { if (vivo) setAutorizado(false); })
      .finally(() => { if (vivo) setChecando(false); });
    return () => { vivo = false; };
  }, []);

  const conteudo = useMemo(() => {
    if (aba === 'layout') return <PainelLayout />;
    if (aba === 'assinatura') return <PainelAssinatura />;
    if (aba === 'enquetes') return <PainelEnquetes />;
    return <GerenciadorRecurso recurso={aba} />;
  }, [aba]);

  if (checando) return <div className="pcfg-loading-full"><FaSpinner className="pcfg-spin" /> Verificando acesso…</div>;

  if (!autorizado) {
    return (
      <div className="pcfg-negado">
        <FaExclamationTriangle />
        <h2>Acesso restrito</h2>
        <p>Você não tem permissão para configurar o Portal Corporativo.</p>
        <button className="pcfg-btn-primary" onClick={() => navigate('/portal')}><FaArrowLeft /> Voltar ao portal</button>
      </div>
    );
  }

  return (
    <div className="pcfg-root">
      <header className="pcfg-topbar">
        <button className="pcfg-back" onClick={() => navigate('/portal')}><FaArrowLeft /> Portal</button>
        <div className="pcfg-topbar__title">
          <h1>Configuração do Portal</h1>
          <p>Gerencie o que aparece para os colaboradores</p>
        </div>
      </header>

      <div className="pcfg-body">
        <nav className="pcfg-tabs">
          {ABAS.map((a) => (
            <button key={a.key} className={`pcfg-tab ${aba === a.key ? 'is-active' : ''}`} onClick={() => setAba(a.key)}>
              {a.icon} <span>{a.label}</span>
            </button>
          ))}
        </nav>
        <main className="pcfg-main">{conteudo}</main>
      </div>
    </div>
  );
}